/**
 * The repository — where a read or a write actually goes.
 *
 * Ported from Csa/offline/csaRepo.js. Offline is opt-in per audit, same
 * design choice as CSA and for the same two reasons:
 *
 *   1. The desk workflow is untouched. Someone writing up on a laptop gets
 *      the same behaviour as before this layer existed, with no local
 *      database quietly accumulating state they never asked for.
 *   2. Provisioning is a deliberate act with a visible result - a silent
 *      cache can't be checked before leaving a site; an explicit "Ready for
 *      offline" can.
 *
 * Domain mapping vs CSA (see outboxRules.js's header for why the sync engine
 * itself didn't change): pillar -> area, sub_pillar/element -> category,
 * question -> checkpoint - one level shallower than CSA, since Electrical's
 * checklist has no equipment tier (confirmed by the live
 * /api/electrical/area-category response). CSA's single yes/partial/no/na
 * answer doesn't fit Electrical, which scores a (criticality, status) pair
 * per checkpoint instead - see saveAnswer and bulkMarkCompliant below.
 */

import * as net from "./net";
import * as db from "./electricalDb";
import { processPhoto } from "./photos";
import {
  newLocalId, makeEntry, selectBatch, applyAcks, summarise,
  applyAnswerLocally, reconcileAnswer,
} from "./outboxRules";

export { newLocalId };

/* ------------------------------------------------------------------ */
/* observers                                                           */
/* ------------------------------------------------------------------ */

const listeners = new Set();
let lastStatus = { state: "synced", pending: 0, waiting: 0, failing: 0, online: true };

export function subscribe(fn) {
  listeners.add(fn);
  fn(lastStatus);
  return () => listeners.delete(fn);
}

function announce(next) {
  lastStatus = next;
  listeners.forEach((fn) => {
    try {
      fn(next);
    } catch (e) {
      /* a broken listener must not stop the others */
    }
  });
}

export const isOnline = () =>
  typeof navigator === "undefined" || navigator.onLine !== false;

async function refreshStatus(auditId, syncing) {
  if (!auditId) return lastStatus;
  const pending = await db.getAll("outbox", "by_audit", auditId).catch(() => []);

  /*
   * Photographs are not outbox entries, so they have to be counted
   * separately or they are invisible to every caller that asks "is this
   * synced".
   */
  const photos = await db.getAll("photos", "by_audit", auditId).catch(() => []);
  const photosPending = photos.filter((p) => p.upload_state !== "uploaded").length;

  const next = summarise(pending, { online: isOnline(), syncing, photosPending });
  announce(next);
  return next;
}

/* ------------------------------------------------------------------ */
/* provisioning                                                        */
/* ------------------------------------------------------------------ */

export async function isProvisioned(auditId) {
  if (!db.isSupported()) return false;
  try {
    const row = await db.get("audits", Number(auditId));
    return !!(row && row.provisioned_at);
  } catch (e) {
    return false;
  }
}

/**
 * Cache everything this audit needs on site, then claim it for this device.
 *
 * Ends in a state the audit list can display. Nothing here is partial: if
 * the framework or the answers fail to store, the audit is not marked
 * provisioned, so it never reads as ready when it is not.
 */
export async function provision(auditId, onProgress) {
  const id = Number(auditId);
  const step = (label, pct) => onProgress && onProgress({ label, pct });

  if (!db.isSupported()) {
    throw new Error("This browser cannot store an audit offline");
  }

  step("Checking storage", 5);
  const room = await db.estimateStorage();
  if (room && room.quota && room.quota - room.usage < 20 * 1024 * 1024) {
    throw new Error("Not enough space left on this device to store an audit");
  }
  await db.requestPersistence();

  step("Downloading the audit", 20);
  const data = await net.getAudit(id);

  step("Downloading the checklist", 45);
  const framework = await net.getFramework(data.audit.framework_version);

  step("Downloading your notes", 65);
  const [notes, extras] = await Promise.all([
    net.listNotes(id).catch(() => []),
    net.listExtras(id).catch(() => []),
  ]);

  step("Saving to this device", 80);
  await db.put("framework", {
    version: framework.version,
    payload: framework,
    cached_at: Date.now(),
  });

  await db.putMany(
    "answers",
    (data.answers || []).map((a) => ({ ...a, audit_id: id, dirty: false }))
  );
  /*
   * notes/extras are keyed in IndexedDB on (audit_id, local_id) - local_id
   * is generated client-side at creation (see net.addNote/addExtra) and
   * sent to the server then, but nothing confirms the server round-trips
   * it back on a plain GET. A row without one would make IndexedDB reject
   * the put outright ("key path yielded a value that is not a valid key"),
   * which is what broke provisioning - falling back to the row's own real
   * id keeps every row's key valid without inventing a second identity
   * scheme for rows that already carry a local_id.
   */
  await db.putMany(
    "notes",
    (Array.isArray(notes) ? notes : []).map((n) => ({
      ...n,
      audit_id: id,
      local_id: n.local_id || String(n.id),
      dirty: false,
    }))
  );
  await db.putMany(
    "extras",
    (extras || []).map((x) => ({
      ...x,
      audit_id: id,
      local_id: x.local_id || String(x.id),
      dirty: false,
    }))
  );

  step("Claiming this audit", 92);
  const device_id = await db.deviceId();
  let claim = null;
  try {
    claim = await net.claimAudit(id, device_id);
  } catch (e) {
    // A claim is advisory. Failing it must not leave the auditor unprovisioned.
    claim = null;
  }

  // Written last: this row is what "ready" means, so it must not exist until
  // everything above has actually landed.
  await db.put("audits", {
    audit_id: id,
    audit: data.audit,
    organization: data.organization,
    site: data.site,
    exclusions: data.exclusions || [],
    summary: data.summary || null,
    framework_version: framework.version,
    provisioned_at: Date.now(),
    device_id,
  });

  step("Ready", 100);
  await refreshStatus(id, false);

  return {
    audit_id: id,
    checkpoints: framework.checkpoint_count,
    answers: (data.answers || []).length,
    notes: (Array.isArray(notes) ? notes : []).length,
    taken_over: !!(claim && claim.taken_over),
    previous_device: claim ? claim.previous_device : null,
    storage: room,
  };
}

/** Drop the local copy. Refuses while anything is still queued. */
export function unprovision(auditId) {
  return db.forgetAudit(Number(auditId));
}

/* ------------------------------------------------------------------ */
/* reads                                                               */
/* ------------------------------------------------------------------ */

export async function getFramework(version) {
  if (db.isSupported() && version) {
    const cached = await db.get("framework", version).catch(() => null);
    if (cached) return cached.payload;
  }
  return net.getFramework(version);
}

/**
 * The audit, assembled from whichever side is authoritative.
 *
 * A provisioned audit reads locally even when the network is up: the local
 * copy includes writes that have not synced yet, so preferring the server
 * would show the auditor stale answers moments after they entered them.
 */
export async function getAudit(auditId) {
  const id = Number(auditId);

  if (await isProvisioned(id)) {
    const [header, answers] = await Promise.all([
      db.get("audits", id),
      db.getAll("answers", "by_audit", id),
    ]);
    await refreshStatus(id, false);
    return {
      audit: header.audit,
      organization: header.organization,
      site: header.site,
      exclusions: header.exclusions || [],
      // An answer row only exists for a checkpoint this audit was actually
      // scoped to in the first place (see provision(), which stores exactly
      // the rows the server sent for this audit) - so the codes already on
      // file here are the same set checkpoints_chk would name, with nothing
      // extra to cache or keep in sync separately.
      checkpoints_chk: answers.map((a) => ({ checkpoint_code: a.checkpoint_code })),
      answers,
      extras: [],
      summary: header.summary || null,
      offline: true,
    };
  }

  return net.getAudit(id);
}

/**
 * Walk the framework tree, collecting checkpoint codes under a level/value.
 *
 * Two levels, not three: area > category > checkpoint, confirmed by the live
 * /api/electrical/area-category response. There is no equipment tier.
 *
 * `area_value` disambiguates a category-level lookup: category names are not
 * unique across areas in the real checklist (e.g. "TR-Signage" appears under
 * several areas), so "mark TR-Signage N/A" means the one under the area the
 * auditor is standing in, not every area that happens to share the name.
 * Required whenever level is "category"; ignored for level "area", where
 * level_value already names the area.
 */
function codesForLevel(framework, level, level_value, area_value) {
  const codes = [];
  let area = null;
  (framework.areas || []).forEach((a) => {
    if (level === "area" && a.area !== level_value) return;
    if (level === "category" && area_value && a.area !== area_value) return;
    (a.categories || []).forEach((cat) => {
      if (level === "category" && cat.category !== level_value) return;
      (cat.checkpoints || []).forEach((cp) => {
        codes.push(cp.code);
        if (area == null) area = a.area;
      });
    });
  });
  return { codes, area };
}

export async function listNotes(auditId, forCode) {
  const id = Number(auditId);
  if (!(await isProvisioned(id))) return net.listNotes(id, forCode);

  const all = await db.getAll("notes", "by_audit", id);
  all.sort((a, b) => String(b.created_at || "").localeCompare(String(a.created_at || "")));
  if (!forCode) return all;

  // The server orders by the checkpoint's category; offline, the framework
  // is cached, so the same ordering is reproduced rather than approximated.
  const header = await db.get("audits", id);
  const fw = await db.get("framework", header.framework_version);
  let category = null;
  if (fw) {
    fw.payload.areas.forEach((a) =>
      (a.categories || []).forEach((cat) =>
        (cat.checkpoints || []).forEach((cp) => {
          if (cp.code === forCode) category = cat.category;
        })
      )
    );
  }
  const unused = all.filter((n) => !n.used_for);
  return {
    suggested: unused
      .filter((n) => category && n.category === category)
      .concat(unused.filter((n) => !(category && n.category === category))),
    used: all.filter((n) => n.used_for),
  };
}

export async function listExtras(auditId) {
  const id = Number(auditId);
  if (!(await isProvisioned(id))) return net.listExtras(id);
  const rows = await db.getAll("extras", "by_audit", id);
  return rows.filter((x) => !x.deleted);
}

/* ------------------------------------------------------------------ */
/* writes                                                              */
/* ------------------------------------------------------------------ */

/**
 * Queue one operation and, if there is a network, send it straight away.
 *
 * The flush is fire-and-forget on purpose: the caller has already been
 * given the local result, and making the UI wait for the server would
 * reintroduce exactly the latency offline capture exists to remove.
 */
async function enqueue(auditId, entity, op, payload) {
  const id = Number(auditId);
  const seq = await db.nextSeq(id);
  const entry = makeEntry({ auditId: id, entity, op, payload, seq });
  await db.put("outbox", entry);
  await refreshStatus(id, false);
  if (isOnline()) flush(id).catch(() => {});
  return entry;
}

export async function saveAnswer(auditId, patch) {
  const id = Number(auditId);
  if (!(await isProvisioned(id))) return net.saveAnswer(id, patch);

  const key = [id, patch.checkpoint_code];
  const current = await db.get("answers", key);
  if (!current) throw new Error("No such checkpoint in this audit");

  // The version queued is the one the server will see when this write
  // replays, which is why the local row is bumped rather than left alone.
  const sending = current.row_version;
  const next = applyAnswerLocally(current, patch, current.answered_by, new Date().toISOString());
  next.audit_id = id;
  next.checkpoint_code = patch.checkpoint_code;
  await db.put("answers", next);

  await enqueue(id, "answer", "save", {
    checkpoint_code: patch.checkpoint_code,
    row_version: sending,
    ...(patch.criticality !== undefined ? { criticality: patch.criticality } : {}),
    ...(patch.status !== undefined ? { status: patch.status } : {}),
    ...(patch.observation !== undefined ? { observation: patch.observation } : {}),
    ...(patch.recommendations !== undefined ? { recommendations: patch.recommendations } : {}),
    ...(patch.legal_reference !== undefined ? { legal_reference: patch.legal_reference } : {}),
    ...(patch.set_by_bulk !== undefined ? { set_by_bulk: patch.set_by_bulk } : {}),
  });

  return next;
}

/**
 * Fill the blanks in one area or category with a compliant status.
 * Electrical's equivalent of CSA's bulkYes - see net.bulkMarkCompliant for
 * why only `status` is set, not `criticality`.
 *
 * `area_value` is required when `level` is "category" - category names are
 * not unique across areas (see codesForLevel), so without it "mark TR-Signage
 * compliant" could reach into a same-named category under a different area.
 */
export async function bulkMarkCompliant(auditId, level, level_value, status, area_value) {
  const id = Number(auditId);
  if (!(await isProvisioned(id)))
    return net.bulkMarkCompliant(id, level, level_value, status, area_value);

  const header = await db.get("audits", id);
  const fw = await db.get("framework", header.framework_version);
  const { codes } = codesForLevel(fw.payload, level, level_value, area_value);
  const compliant = status || "YES";

  const rows = await db.getAll("answers", "by_audit", id);
  const blanks = rows.filter((a) => codes.includes(a.checkpoint_code) && !a.status);

  // Applied locally with exactly the server's rule - fill blanks only, never
  // overwrite a considered answer.
  await db.putMany(
    "answers",
    blanks.map((a) => ({ ...a, status: compliant, set_by_bulk: true, row_version: a.row_version + 1, dirty: true }))
  );

  await enqueue(id, "answer", "bulk_compliant", { level, level_value, area: area_value, status: compliant });

  return {
    level, level_value,
    checkpoints_covered: codes.length,
    answered: blanks.length,
    left_untouched: codes.length - blanks.length,
  };
}

export async function addNote(auditId, body, category) {
  const id = Number(auditId);
  const local_id = newLocalId();
  if (!(await isProvisioned(id))) return net.addNote(id, body, category);

  const row = {
    audit_id: id, local_id, body,
    category: category || null,
    used_for: null,
    created_at: new Date().toISOString(),
    dirty: true,
  };
  await db.put("notes", row);
  await enqueue(id, "note", "create", { local_id, body, category: category || null, created_at: row.created_at });
  return row;
}

export async function useNote(auditId, noteId, checkpoint_code) {
  const id = Number(auditId);
  if (!(await isProvisioned(id))) return net.useNote(id, noteId, checkpoint_code);

  // Offline, a note is addressed by local_id — it may have no server id yet.
  const notes = await db.getAll("notes", "by_audit", id);
  const note = notes.find((n) => n.local_id === noteId || n.id === noteId);
  if (!note) throw new Error("No such note");

  const answer = await db.get("answers", [id, checkpoint_code]);
  if (!answer) throw new Error("No such checkpoint in this audit");

  const existing = String(answer.observation || "").trim();
  const merged = existing && existing !== note.body ? existing + " " + note.body : note.body;

  await db.put("answers", { ...answer, observation: merged, row_version: answer.row_version + 1, dirty: true });
  await db.put("notes", { ...note, used_for: checkpoint_code, dirty: true });

  await enqueue(id, "note", "use", { local_id: note.local_id, checkpoint_code });
  return { note_id: note.id || null, local_id: note.local_id, checkpoint_code, observation: merged };
}

export async function deleteNote(auditId, noteId) {
  const id = Number(auditId);
  if (!(await isProvisioned(id))) return net.deleteNote(id, noteId);

  const notes = await db.getAll("notes", "by_audit", id);
  const note = notes.find((n) => n.local_id === noteId || n.id === noteId);
  if (!note) return { deleted: false };

  await db.del("notes", [id, note.local_id]);
  await enqueue(id, "note", "delete", { local_id: note.local_id });
  return { deleted: true };
}

export async function addExtra(auditId, extra) {
  const id = Number(auditId);
  if (!(await isProvisioned(id))) return net.addExtra(id, extra);

  const local_id = newLocalId();
  const row = {
    ...extra, audit_id: id, local_id,
    created_at: new Date().toISOString(), dirty: true,
  };
  await db.put("extras", row);
  await enqueue(id, "extra", "create", { ...extra, local_id, created_at: row.created_at });
  return row;
}

export async function updateExtra(auditId, extraId, patch) {
  const id = Number(auditId);
  if (!(await isProvisioned(id))) return net.updateExtra(id, extraId, patch);

  const rows = await db.getAll("extras", "by_audit", id);
  const row = rows.find((x) => x.local_id === extraId || x.id === extraId);
  if (!row) throw new Error("No such observation");

  const next = { ...row, ...patch, dirty: true };
  await db.put("extras", next);
  await enqueue(id, "extra", "update", { ...patch, local_id: row.local_id });
  return next;
}

export async function deleteExtra(auditId, extraId) {
  const id = Number(auditId);
  if (!(await isProvisioned(id))) return net.deleteExtra(id, extraId);

  const rows = await db.getAll("extras", "by_audit", id);
  const row = rows.find((x) => x.local_id === extraId || x.id === extraId);
  if (!row) return { deleted: false };

  await db.del("extras", [id, row.local_id]);
  await enqueue(id, "extra", "delete", { local_id: row.local_id });
  return { deleted: true };
}

export async function addExclusion(auditId, exclusion) {
  const id = Number(auditId);
  if (!(await isProvisioned(id))) return net.addExclusion(id, exclusion);

  const header = await db.get("audits", id);
  const fw = await db.get("framework", header.framework_version);
  const { codes, area } = codesForLevel(fw.payload, exclusion.level, exclusion.level_value, exclusion.area);

  const rows = await db.getAll("answers", "by_audit", id);
  const blanks = rows.filter((a) => codes.includes(a.checkpoint_code) && !a.status);
  await db.putMany(
    "answers",
    blanks.map((a) => ({ ...a, status: "N/A", na_source: exclusion.level, dirty: true }))
  );

  const local = {
    id: "local-" + newLocalId().slice(0, 8),
    audit_id: id, area,
    ...exclusion,
    created_at: new Date().toISOString(),
  };
  await db.put("audits", { ...header, exclusions: [...(header.exclusions || []), local] });

  await enqueue(id, "exclusion", "create", exclusion);

  return {
    exclusion: local,
    checkpoints_covered: codes.length,
    marked_na: blanks.length,
    left_untouched: rows
      .filter((a) => codes.includes(a.checkpoint_code) && a.status)
      .map((a) => a.checkpoint_code),
  };
}

export async function removeExclusion(auditId, exclusionId) {
  const id = Number(auditId);
  if (!(await isProvisioned(id))) return net.removeExclusion(id, exclusionId);

  const header = await db.get("audits", id);
  const ex = (header.exclusions || []).find((e) => String(e.id) === String(exclusionId));
  if (!ex) return { restored: 0 };

  const rows = await db.getAll("answers", "by_audit", id);
  const restore = rows.filter((a) => a.na_source === ex.level);
  await db.putMany("answers", restore.map((a) => ({ ...a, status: null, na_source: null, dirty: true })));

  await db.put("audits", {
    ...header,
    exclusions: (header.exclusions || []).filter((e) => String(e.id) !== String(exclusionId)),
  });

  await enqueue(id, "exclusion", "delete", { level_value: ex.level_value });
  return { restored: restore.length };
}

/* ------------------------------------------------------------------ */
/* photos                                                              */
/* ------------------------------------------------------------------ */

/**
 * Capture one photo.
 *
 * Downscaled before it is stored, never after — the full-size blob must not
 * reach IndexedDB even briefly, because the quota it would consume is the
 * thing that fails on site.
 *
 * `checkpoint_code` is optional - a photo can be taken on the checkpoint when
 * the auditor is already there, or into the camera roll when they're
 * walking past several things in a couple of minutes, and assigned later.
 * Submit refuses while any roll photo is still unassigned.
 */
export async function capturePhoto(auditId, file, checkpoint_code) {
  const id = Number(auditId);
  const local_id = newLocalId();
  const processed = await processPhoto(file);

  if (!(await isProvisioned(id))) {
    // Not provisioned: upload straight away, exactly as the desk flow does
    // for everything else.
    const row = await net.uploadPhoto(id, {
      local_id,
      blob: processed.blob,
      thumb: processed.thumb,
      checkpoint_code: checkpoint_code || null,
      captured_at: new Date().toISOString(),
    });
    return { ...row, ...processed, local_id };
  }

  const room = await db.estimateStorage();
  if (room && room.pct >= 95) {
    throw new Error("This device is nearly full. Sync now to free up space.");
  }

  const row = {
    audit_id: id,
    local_id,
    blob: processed.blob,
    thumb: processed.thumb,
    width: processed.width,
    height: processed.height,
    bytes: processed.bytes,
    upload_state: "local",
    checkpoint_codes: checkpoint_code ? [checkpoint_code] : [],
    captured_at: new Date().toISOString(),
    s3_url: null,
    thumb_url: null,
  };
  await db.put("photos", row);

  /*
   * The link is queued; the bytes are not. An upload carries a body the
   * JSON outbox cannot hold, so uploads run as their own pass once the
   * outbox has drained — which is also the required order, since a link
   * references the row the upload creates.
   */
  if (checkpoint_code) {
    await enqueue(id, "photo", "link", { local_id, checkpoint_code });
  } else {
    await refreshStatus(id, false);
    if (isOnline()) flush(id).catch(() => {});
  }

  return row;
}

export async function listPhotos(auditId) {
  const id = Number(auditId);
  if (!(await isProvisioned(id))) return net.listPhotos(id);
  const rows = await db.getAll("photos", "by_audit", id);
  return rows.sort((a, b) =>
    String(a.captured_at || "").localeCompare(String(b.captured_at || ""))
  );
}

export async function linkPhoto(auditId, local_id, checkpoint_code) {
  const id = Number(auditId);
  if (!(await isProvisioned(id))) return net.linkPhoto(id, local_id, checkpoint_code);

  const row = await db.get("photos", [id, local_id]);
  if (!row) throw new Error("No such photo");

  const codes = row.checkpoint_codes || [];
  if (codes.includes(checkpoint_code)) return row;

  const next = { ...row, checkpoint_codes: [...codes, checkpoint_code] };
  await db.put("photos", next);
  await enqueue(id, "photo", "link", { local_id, checkpoint_code });
  return next;
}

export async function unlinkPhoto(auditId, local_id, checkpoint_code) {
  const id = Number(auditId);
  if (!(await isProvisioned(id))) return net.unlinkPhoto(id, local_id, checkpoint_code);

  const row = await db.get("photos", [id, local_id]);
  if (!row) throw new Error("No such photo");

  // The photo returns to the camera roll rather than being deleted: this is
  // how an auditor moves evidence from one checkpoint to another.
  const next = {
    ...row,
    checkpoint_codes: (row.checkpoint_codes || []).filter((c) => c !== checkpoint_code),
  };
  await db.put("photos", next);
  await enqueue(id, "photo", "unlink", { local_id, checkpoint_code });
  return next;
}

export async function deletePhoto(auditId, local_id) {
  const id = Number(auditId);
  if (!(await isProvisioned(id))) return net.deletePhoto(id, local_id);

  await db.del("photos", [id, local_id]);
  await enqueue(id, "photo", "delete", { local_id });
  return { deleted: true };
}

/**
 * Upload every photo still held locally.
 *
 * Runs after the outbox drains: answers first (small, fast), then photos.
 * One request per photo, so a connection dropping at photo 140 of 300
 * resumes at 141 rather than restarting.
 *
 * Purge after upload: once the server acknowledges, the display blob is
 * dropped and only the thumbnail is kept. The camera roll still renders,
 * the full image is one URL away, and the quota stops growing.
 */
export async function uploadPhotos(auditId, onProgress) {
  const id = Number(auditId);
  if (!isOnline()) return { uploaded: 0, offline: true };

  const all = await db.getAll("photos", "by_audit", id);
  const todo = all.filter((p) => p.upload_state !== "uploaded" && p.blob);
  if (!todo.length) return { uploaded: 0, remaining: 0 };

  let uploaded = 0;
  let failed = 0;

  for (let i = 0; i < todo.length; i++) {
    const row = todo[i];
    if (onProgress) onProgress({ done: i, total: todo.length, local_id: row.local_id });

    try {
      const result = await net.uploadPhoto(id, {
        local_id: row.local_id,
        blob: row.blob,
        thumb: row.thumb,
        checkpoint_code: (row.checkpoint_codes || [])[0] || null,
        captured_at: row.captured_at,
      });

      await db.put("photos", {
        ...row,
        blob: null, // purged — the thumbnail and the URL are enough from here
        s3_url: result.s3_url,
        thumb_url: result.thumb_url,
        upload_state: "uploaded",
        last_error: null,
      });
      uploaded++;
    } catch (err) {
      // Recorded and skipped. One bad photo must not stop the rest.
      await db.put("photos", { ...row, upload_state: "failed", last_error: err.message });
      failed++;
    }
  }

  if (onProgress) onProgress({ done: todo.length, total: todo.length });
  await refreshStatus(id, false);
  return { uploaded, failed, remaining: todo.length - uploaded };
}

/**
 * How full this device is. Checked at provisioning and while capturing -
 * discovering the quota is full at photo 200, on site, is unrecoverable.
 */
export const storageEstimate = db.estimateStorage;

/**
 * Upload what is pending, then drop the display blobs that are already
 * safely on the server. The thumbnails stay, so the camera roll still
 * renders. This is the "sync and purge" the quota warning offers.
 */
export async function syncAndFreeSpace(auditId) {
  const id = Number(auditId);
  await flush(id);
  const result = await uploadPhotos(id);

  const rows = await db.getAll("photos", "by_audit", id);
  let freed = 0;
  for (const row of rows) {
    if (row.upload_state === "uploaded" && row.blob) {
      freed += row.bytes || 0;
      await db.put("photos", { ...row, blob: null });
    }
  }
  return { ...result, freed_bytes: freed };
}

/** Photos with no checkpoint - submit refuses to proceed past these. */
export async function unassignedPhotos(auditId) {
  const rows = await listPhotos(auditId);
  return rows.filter((p) => !(p.checkpoint_codes || []).length);
}

/* ------------------------------------------------------------------ */
/* sync                                                                */
/* ------------------------------------------------------------------ */

const inFlight = new Set();

/**
 * Replay the outbox for one audit.
 *
 * Guarded against overlap: two concurrent flushes would send the same
 * entries twice. The idempotency key makes that safe on the server, but it
 * wastes a connection at exactly the moment several devices want it.
 */
export async function flush(auditId) {
  const id = Number(auditId);
  if (inFlight.has(id)) return { skipped: true };
  if (!isOnline()) return { offline: true };

  inFlight.add(id);
  try {
    const all = await db.getAll("outbox", "by_audit", id);
    if (!all.length) {
      await refreshStatus(id, false);
      return { applied: 0, pending: 0, conflicts: [] };
    }

    await refreshStatus(id, true);

    const batch = selectBatch(all, {});
    if (!batch.length) {
      await refreshStatus(id, false);
      return { applied: 0, pending: all.length, conflicts: [] };
    }

    const device_id = await db.deviceId();
    let response;
    try {
      response = await net.syncAudit(
        id,
        device_id,
        batch.map((e) => ({ seq: e.seq, key: e.key, entity: e.entity, op: e.op, payload: e.payload }))
      );
    } catch (err) {
      /*
       * The whole request failed — no acks, so nothing is cleared. Every
       * entry is kept and retried later, which is the point of clearing
       * only on an explicit acknowledgement.
       */
      await refreshStatus(id, false);
      return { error: err.message, applied: 0, pending: all.length, conflicts: [] };
    }

    const outcome = applyAcks(batch, response.results, {});

    if (outcome.clear.length) await db.delMany("outbox", outcome.clear);

    if (outcome.retry.length) {
      const byKey = new Map(batch.map((e) => [e.key, e]));
      await db.putMany(
        "outbox",
        outcome.retry.map((u) => ({ ...byKey.get(u.key), ...u }))
      );
    }

    // Adopt server state for anything acknowledged, so local row_versions
    // stay in step with the rows the next write will target.
    for (const ack of outcome.applied) {
      if (ack.entity === "answer" && ack.op === "save" && ack.result && ack.result.checkpoint_code) {
        const local = await db.get("answers", [id, ack.result.checkpoint_code]);
        const merged = reconcileAnswer(local, { ...ack.result, audit_id: id });
        await db.put("answers", merged);
      }
      if (ack.entity === "note" && ack.op === "create" && ack.result && ack.result.local_id) {
        const local = await db.get("notes", [id, ack.result.local_id]);
        if (local) await db.put("notes", { ...local, id: ack.result.id, dirty: false });
      }
      if (ack.entity === "extra" && ack.op === "create" && ack.result && ack.result.local_id) {
        const local = await db.get("extras", [id, ack.result.local_id]);
        if (local) await db.put("extras", { ...local, id: ack.result.id, dirty: false });
      }
    }

    /*
     * Answers first, then photos. Only once the outbox is actually empty,
     * so a queued link can never arrive before the upload that creates the
     * row it points at.
     */
    let photos = null;
    const drained = await db.getAll("outbox", "by_audit", id);
    if (!drained.length) {
      photos = await uploadPhotos(id).catch(() => null);
    }

    const status = await refreshStatus(id, false);
    return {
      applied: response.applied,
      already_applied: response.already_applied,
      pending: status.pending,
      conflicts: outcome.conflicts,
      photos,
    };
  } finally {
    inFlight.delete(id);
  }
}

/** Everything queued for one audit, for the sync screen. */
export function pending(auditId) {
  return db.getAll("outbox", "by_audit", Number(auditId));
}

/**
 * Discard one queued operation.
 *
 * Only ever for a conflict the auditor has chosen to abandon — nothing
 * calls this automatically, because a dropped entry is work that never
 * reaches the report.
 */
export async function discard(auditId, key) {
  await db.del("outbox", key);
  return refreshStatus(Number(auditId), false);
}

/** Pull the server's copy back down after syncing, so scores refresh. */
export async function resync(auditId) {
  const id = Number(auditId);
  if (!(await isProvisioned(id))) return null;

  const data = await net.getAudit(id);
  const header = await db.get("audits", id);
  await db.put("audits", {
    ...header,
    audit: data.audit,
    organization: data.organization,
    site: data.site,
    exclusions: data.exclusions || [],
    summary: data.summary || null,
  });
  await db.putMany("answers", (data.answers || []).map((a) => ({ ...a, audit_id: id, dirty: false })));
  return data;
}

/* ------------------------------------------------------------------ */
/* reconnection                                                        */
/* ------------------------------------------------------------------ */

let watching = false;

/**
 * Flush every audit that has queued work when the network returns.
 *
 * Registered once from the app shell. `online` is not entirely trustworthy
 * — it fires for a captive portal too — but a failed flush is harmless and
 * retried, so an optimistic trigger costs nothing.
 */
export function watchConnection() {
  if (watching || typeof window === "undefined") return;
  watching = true;

  const flushAll = async () => {
    try {
      const all = await db.getAll("outbox");
      const ids = [...new Set(all.map((e) => e.audit_id))];
      for (const id of ids) await flush(id);
    } catch (e) {
      /* nothing to do; the next event tries again */
    }
  };

  window.addEventListener("online", flushAll);
  window.addEventListener("offline", () => announce({ ...lastStatus, state: "offline", online: false }));
  if (isOnline()) flushAll();
}
